import { lazy } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

// Auth Routes
const Login = lazy(() => import('@/pages/auth/Login.jsx'));
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword.jsx'));
const AutoLogin = lazy(() => import('@/pages/auth/AutoLogin.jsx'));

// App Routes
const Configuration = lazy(() => import('@/pages/admin/Configuration.jsx'));
const ConfigVersions = lazy(() => import('@/pages/admin/configuration/ConfigVersions.jsx'));
const ConfigIslands = lazy(() => import('@/pages/admin/configuration/ConfigIslands.jsx'));
const ConfigPoints = lazy(() => import('@/pages/admin/configuration/ConfigPoints.jsx'));
const ConfigScales = lazy(() => import('@/pages/admin/configuration/ConfigScales.jsx'));
const ConfigExamples = lazy(() => import('@/pages/admin/configuration/ConfigExamples.jsx'));
const ConfigStrong = lazy(() => import('@/pages/admin/configuration/ConfigStrong.jsx'));
const AdminGameManagement = lazy(() => import('@/pages/admin/GameManagement.jsx'));
const Buyers = lazy(() => import('@/pages/admin/Buyers.jsx'));
const BuyersEnterprises = lazy(() => import('@/pages/admin/buyers/BuyersEnterprises.jsx'));
const BuyersList = lazy(() => import('@/pages/admin/buyers/BuyersList.jsx'));
const Coaches = lazy(() => import('@/pages/admin/Coaches.jsx'));
const CoachesList = lazy(() => import('@/pages/admin/coaches/CoachesList.jsx'));
const Interventions = lazy(() => import('@/pages/admin/coaches/Interventions.jsx'));
const AnimatorsList = lazy(() => import('@/pages/admin/AnimatorsList.jsx'));
const PlayersList = lazy(() => import('@/pages/admin/PlayersList.jsx'));
const AdminAnimator = lazy(() => import('@/pages/admin/AdminAnimator.jsx'));
const History = lazy(() => import('@/pages/admin/History.jsx'));
const RunningGames = lazy(() => import('@/pages/admin/history/RunningGames.jsx'));
const TerminatedGames = lazy(() => import('@/pages/admin/history/TerminatedGames.jsx'));
const AdminResponseAnalysis = lazy(() => import('@/pages/admin/history/results/ResponseAnalysis.jsx'));
const AdminAlignmentStudy = lazy(() => import('@/pages/admin/history/results/AlignmentStudy.jsx'));
const AdminBinomesJustifications = lazy(() => import('@/pages/admin/history/results/BinomesJustifications.jsx'));
const AdminAIAnalysis = lazy(() => import('@/pages/admin/history/results/AIAnalysis.jsx'));
const Overview = lazy(() => import('@/pages/buyer/Overview.jsx'));
const GameManagement = lazy(() => import('@/pages/buyer/GameManagement.jsx'));
const GameManagementPurchase = lazy(() => import('@/pages/buyer/GameManagementPurchase.jsx'));
const GameManagementAnimatorView = lazy(() => import('@/pages/buyer/GameManagementAnimatorView.jsx'));
const AnimatorManagement = lazy(() => import('@/pages/buyer/AnimatorManagement.jsx'));
const BuyerResponseAnalysis = lazy(() => import('@/pages/buyer/results/ResponseAnalysis.jsx'));
const BuyerAlignmentStudy = lazy(() => import('@/pages/buyer/results/AlignmentStudy.jsx'));
const BuyerBinomesJustifications = lazy(() => import('@/pages/buyer/results/BinomesJustifications.jsx'));
const BuyerAIAnalysis = lazy(() => import('@/pages/buyer/results/AIAnalysis.jsx')); 
const AnimatorDashboard = lazy(() => import('@/pages/animator/AnimatorDashboard.jsx'));
const AnimatorOverview = lazy(() => import('@/pages/animator/Overview.jsx'));
const PlayerManagement = lazy(() => import('@/pages/animator/PlayerManagement.jsx'));
const GameSessions = lazy(() => import('@/pages/animator/GameSessions.jsx'));
const AnimatorTerminatedGames = lazy(() => import('@/pages/animator/history/TerminatedGames.jsx'));
const ResponseAnalysis = lazy(() => import('@/pages/animator/results/ResponseAnalysis.jsx'));
const AlignmentStudy = lazy(() => import('@/pages/animator/results/AlignmentStudy.jsx'));
const BinomesJustifications = lazy(() => import('@/pages/animator/results/BinomesJustifications.jsx'));
const AIAnalysis = lazy(() => import('@/pages/animator/results/AIAnalysis.jsx')); 
const GameLayout = lazy(() => import('../layouts/GameLayout.jsx'));
const LoginScreen = lazy(() => import('../pages/game/LoginScreen.jsx'));
const StartScreen = lazy(() => import('../pages/game/StartScreen.jsx'));
const DiceResultInputScreen = lazy(() => import('../pages/game/DiceResultInputScreen.jsx'));
const PartnerChoiceScreen = lazy(() => import('../pages/game/PartnerChoiceScreen.jsx'));
const SectorSelectionScreen = lazy(() => import('../pages/game/SectorSelectionScreen.jsx'));
const TurnWaitingScreen = lazy(() => import('../pages/game/TurnWaitingScreen.jsx'));
const WaitingForResponsesScreen = lazy(() => import('../pages/game/WaitingForResponsesScreen.jsx'));
const StatusScreen = lazy(() => import('../pages/game/StatusScreen.jsx'));
const CoachDashboard = lazy(() => import('@/pages/coach/CoachDashboard.jsx'));
const CoachGameManagement = lazy(() => import('@/pages/coach/CoachGameManagement.jsx'));
const DashboardRedirect = lazy(() => import('@/router/DashboardRedirect.jsx'));

export const authRoutes = [
  { path: 'login', name: 'Login', element: <Login /> },
  { path: 'forgot-password', name: 'Forgot Password', element: <ForgotPassword /> },
  { path: 'auto-login', name: 'Auto Login', element: <AutoLogin /> },
];

export const gameLoginRoute = [
  {
    path: 'game/login',
    name: 'Game Login',
    element: <LoginScreen />,
  },
  {
    path: 'game/status',
    name: 'Game Status',
    element: <StatusScreen />,
  }
];

export const gameRoutes = [
  {
    path: 'game/:gameRunId/*',
    name: 'Game',
    element: <GameLayout />,
    children: [
      { path: 'start', name: 'Game Start', element: <StartScreen /> },
      { path: 'dice-input', name: 'Dice Input', element: <DiceResultInputScreen /> },
      { path: 'partner-choice', name: 'Partner Choice', element: <PartnerChoiceScreen /> },
      { path: 'sector-selection', name: 'Sector Selection', element: <SectorSelectionScreen /> },
      { path: 'turn-waiting', name: 'Turn Waiting', element: <TurnWaitingScreen /> },
      { path: 'waiting-for-responses', name: 'Waiting for Responses', element: <WaitingForResponsesScreen /> },
    ]
  }
];

export const appRoutes = [
  {
    path: 'dashboard/*',
    name: 'Dashboard',
    element: <Outlet />,
    children: [
      {
        path: '',
        name: 'Dashboard Root',
        element: <DashboardRedirect />,
      },
      {
        path: 'configuration',
        name: 'Configuration',
        element: <Configuration />,
      },
      {
        path: 'configuration/versions',
        name: 'Config Versions',
        element: <ConfigVersions />,
      },
      {
        path: 'configuration/islands',
        name: 'Config Islands',
        element: <ConfigIslands />,
      },
      {
        path: 'configuration/points',
        name: 'Config Points',
        element: <ConfigPoints />,
      },
      {
        path: 'configuration/scales',
        name: 'Config Scales',
        element: <ConfigScales />,
      },
      {
        path: 'configuration/examples',
        name: 'Config Examples',
        element: <ConfigExamples />,
      },
      {
        path: 'configuration/strong',
        name: 'Config Strong',
        element: <ConfigStrong />,
      },
      {
        path: 'game-management/tokens',
        name: 'Game Management Tokens',
        element: <AdminGameManagement />,
      },
      {
        path: 'buyers',
        name: 'Buyers',
        element: <Buyers />,
      },
      {
        path: 'buyers/enterprises',
        name: 'Buyers Enterprises',
        element: <BuyersEnterprises />,
      },
      {
        path: 'buyers/list',
        name: 'Buyers List',
        element: <BuyersList />,
      },
      {
        path: 'animators/list',
        name: 'Animators List',
        element: <AnimatorsList />,
      },
      {
        path: 'players/list',
        name: 'Players List',
        element: <PlayersList />,
      },
      {
        path: 'coaches',
        name: 'Coaches',
        element: <Coaches />,
      },
      {
        path: 'coaches/list',
        name: 'Coaches List',
        element: <CoachesList />,
      },
      {
        path: 'coaches/interventions',
        name: 'Interventions',
        element: <Interventions />,
      },
      {
        path: 'history',
        name: 'History',
        element: <History />,
      },
      {
        path: 'history/running',
        name: 'Running Games',
        element: <RunningGames />,
      },
      {
        path: 'history/terminated',
        name: 'Terminated Games',
        element: <TerminatedGames />,
      },
      {
        path: 'history/response-analysis',
        name: 'Admin Response Analysis',
        element: <AdminResponseAnalysis />,
      },
      {
        path: 'history/alignment-study',
        name: 'Admin Alignment Study',
        element: <AdminAlignmentStudy />,
      },
      {
        path: 'history/binomes-justifications',
        name: 'Admin Binomes Justifications',
        element: <AdminBinomesJustifications />,
      },
      {
        path: 'history/ai-analysis',
        name: 'Admin AI Analysis',
        element: <AdminAIAnalysis />,
      },
      {
        path: 'admin-animator',
        name: 'Admin Animator',
        element: <AdminAnimator />,
      },
      {
        path: 'coach',
        name: 'Coach Dashboard',
        element: <CoachDashboard />,
      },
      {
        path: 'coach/game-management',
        name: 'Coach Game Management',
        element: <CoachGameManagement />,
      },
      {
        path: 'overview',
        name: 'Overview',
        element: <Overview />,
      },
      {
        path: 'game-management',
        name: 'Game Management',
        element: <GameManagement />,
      },
      {
        path: 'game-management/achats',
        name: 'Game Management - Achats',
        element: <GameManagementPurchase />,
      },
      {
        path: 'game-management/animateur',
        name: 'Game Management - Vue Animateur',
        element: <GameManagementAnimatorView />,
      },
      {
        path: 'animator-management',
        name: 'Animator Management',
        element: <AnimatorManagement />,
      },
      {
        path: 'buyer/response-analysis',
        name: 'Buyer Response Analysis',
        element: <BuyerResponseAnalysis />,
      },
      {
        path: 'buyer/alignment-study',
        name: 'Buyer Alignment Study',
        element: <BuyerAlignmentStudy />,
      },
      {
        path: 'buyer/binomes-justifications',
        name: 'Buyer Binomes Justifications',
        element: <BuyerBinomesJustifications />,
      },
      {
        path: 'buyer/ai-analysis',
        name: 'Buyer AI Analysis',
        element: <BuyerAIAnalysis />,
      },
      {
        path: 'animator',
        name: 'Animator Dashboard',
        element: <AnimatorDashboard />,
      },
      {
        path: 'animator-overview',
        name: 'Animator Overview',
        element: <AnimatorOverview />,
      },
      {
        path: 'player-management',
        name: 'Player Management',
        element: <PlayerManagement />,
      },
      {
        path: 'game-sessions',
        name: 'Game Sessions',
        element: <GameSessions />,
      },
      {
        path: 'animator/response-analysis',
        name: 'Response Analysis',
        element: <ResponseAnalysis />,
      },
      {
        path: 'animator/alignment-study',
        name: 'Alignment Study',
        element: <AlignmentStudy />,
      },
      {
        path: 'animator/binomes-justifications',
        name: 'Binomes Justifications',
        element: <BinomesJustifications />,
      },
      {
        path: 'animator/ai-analysis',
        name: 'AI Analysis',
        element: <AIAnalysis />,
      },
      {
        path: 'animator/history/terminated',
        name: 'Animator Terminated Games',
        element: <AnimatorTerminatedGames />,
      },
    ]
  },
  {
    path: '/',
    name: 'root',
    element: <Navigate to="/dashboard" />
  },
];